import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiSmartphone, FiUsers, FiClock, FiCheck, FiAlertCircle, FiHistory } = FiIcons;

const SmsCampaign = () => {
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [history, setHistory] = useState([]);

  // Load History
  useEffect(() => {
    const storedHistory = localStorage.getItem('ramirez_sms_history');
    if (storedHistory) setHistory(JSON.parse(storedHistory));
  }, []);

  const handleSend = (e) => {
    e.preventDefault();
    setIsSending(true);

    // Simulate API call
    setTimeout(() => {
      setIsSending(false);
      setSentSuccess(true);
      
      const newEntry = {
          id: Date.now(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString(),
          message: message,
          audience: audience,
          status: 'Delivered'
      };

      const updatedHistory = [newEntry, ...history];
      setHistory(updatedHistory);
      localStorage.setItem('ramirez_sms_history', JSON.stringify(updatedHistory));

      setMessage('');
      setTimeout(() => setSentSuccess(false), 3000);
    }, 2000);
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">SMS Campaigns</h1>
        <p className="text-gray-400">Send mass text message alerts via Twilio.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Compose Section */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <SafeIcon icon={FiSend} className="text-blue-500" />
              Compose SMS
            </h2>
            <form onSubmit={handleSend} className="space-y-6">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Target Audience
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <label className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center gap-2 transition-all ${audience === 'all' ? 'bg-blue-600 border-blue-500 text-white shadow-lg' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="all" className="hidden" checked={audience === 'all'} onChange={(e) => setAudience(e.target.value)} />
                    <SafeIcon icon={FiUsers} className="text-2xl" />
                    <span className="font-bold text-sm">All Subscribers</span>
                  </label>
                  <label className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center gap-2 transition-all ${audience === 'due' ? 'bg-blue-600 border-blue-500 text-white shadow-lg' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="due" className="hidden" checked={audience === 'due'} onChange={(e) => setAudience(e.target.value)} />
                    <SafeIcon icon={FiClock} className="text-2xl" />
                    <span className="font-bold text-sm">Due Soon</span>
                  </label>
                  <label className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center gap-2 transition-all ${audience === 'urgent' ? 'bg-blue-600 border-blue-500 text-white shadow-lg' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="urgent" className="hidden" checked={audience === 'urgent'} onChange={(e) => setAudience(e.target.value)} />
                    <SafeIcon icon={FiAlertCircle} className="text-2xl" />
                    <span className="font-bold text-sm">Urgent/Expired</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Message Content
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here... (e.g., 'Reminder: Your fleet inspection is due next week. Call Ramirez Mobile to schedule.')"
                    rows="5"
                    maxLength="160"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                    required
                  ></textarea>
                  <div className="absolute bottom-3 right-3 text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                    {message.length}/160 chars
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-700">
                <button
                  type="submit"
                  disabled={isSending || !message.trim()}
                  className={`w-full font-bold py-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg ${isSending ? 'bg-gray-600 cursor-not-allowed text-gray-300' : sentSuccess ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-blue-900/50'}`}
                >
                  {isSending ? (
                    <>Processing...</>
                  ) : sentSuccess ? (
                    <>
                      <SafeIcon icon={FiCheck} className="text-xl" />
                      <span>Sent Successfully!</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="text-xl" />
                      <span>Send Broadcast</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* History */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
             <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiHistory} className="text-gray-400" />
              Recent Campaigns
            </h2>
            <div className="space-y-4 max-h-60 overflow-y-auto">
                {history.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No campaigns sent yet.</p>
                ) : (
                    history.map(item => (
                        <div key={item.id} className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-gray-400">{item.date} {item.time}</span>
                                <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded border border-green-800">{item.status}</span>
                            </div>
                            <p className="text-sm text-white truncate">{item.message}</p>
                            <p className="text-xs text-blue-400 mt-1">To: {item.audience}</p>
                        </div>
                    ))
                )}
            </div>
          </div>
        </div>

        {/* Device Preview */}
        <div className="lg:col-span-1 flex justify-center lg:justify-start">
          <div className="w-[280px] h-[580px] bg-black rounded-[3rem] border-[6px] border-gray-800 relative overflow-hidden shadow-2xl bg-gray-900">
            {/* Notch */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-xl z-20"></div>
            {/* Screen */}
            <div className="w-full h-full bg-[#1e293b] pt-12 px-3 flex flex-col font-sans">
              <div className="text-center text-gray-500 text-[10px] mb-4">Today 9:41 AM</div>
              {/* Incoming Message Bubble */}
              <div className="self-start max-w-[85%] bg-[#374151] rounded-2xl rounded-tl-none px-4 py-3 mb-4 shadow-sm">
                <p className="text-gray-200 text-xs leading-relaxed">
                  {message || "Your message preview will appear here in real-time as you type..."}
                </p>
              </div>
              <div className="mt-auto mb-8 text-center">
                <p className="text-[10px] text-gray-500">Sent via Ramirez Mobile Alerts</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SmsCampaign;